import { inject, Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { createRequestOption, mapAsBody } from 'app/shared/util/request-util';
import { Offer } from '../models/offer.model';
import { IBaryTableFetchService } from './i-bary-table-fetch-service';
import { API_ENDPOINTS } from '../models/enums/endpoints.enum';
import { ApiProviderService } from './api-provider.service';
import { OfferViewDTO } from '../models/offer-view-dto.type';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class OfferService implements IBaryTableFetchService<OfferViewDTO> {
  readonly #apiProvider = inject(ApiProviderService);

  private resourceUrl = API_ENDPOINTS.OFFER;
  private emptyUrl = API_ENDPOINTS.OFFER_EMPTY;
  private offerUrl = API_ENDPOINTS.OFFER_TRANSPORTER_ID;
  private insuranceItemsUrl = API_ENDPOINTS.OFFER_ID_INSURANCE_ITEMS;
  private offerId = API_ENDPOINTS.OFFER_ID;
  private offerDuplicateUrl = API_ENDPOINTS.OFFER_ID_DUPLICATE;

  public find(id: number): Observable<OfferViewDTO> {
    return this.#apiProvider.get<OfferViewDTO>(`/offer/${id}`, { observe: 'response' }).pipe(
      mapAsBody,
      map((responses: OfferViewDTO) => new OfferViewDTO(responses))
    );
  }

  public findQuotationOffer(): Observable<OfferViewDTO> {
    return this.#apiProvider.get<OfferViewDTO>('/offer/quotation', { observe: 'response' }).pipe(
      mapAsBody,
      map((responses: OfferViewDTO) => new OfferViewDTO(responses))
    );
  }

  public save(offer: OfferViewDTO): Observable<OfferViewDTO> {
    if (offer.id) {
      return this.#apiProvider.put<OfferViewDTO>(this.offerId(offer.id), offer.serialize()).pipe(
        mapAsBody,
        map((responses: OfferViewDTO) => new OfferViewDTO(responses))
      );
    }
    return this.#apiProvider.post<OfferViewDTO>(this.emptyUrl, offer.serialize()).pipe(
      mapAsBody,
      map((responses: OfferViewDTO) => new OfferViewDTO(responses))
    );
  }

  public fillDataToShow(entities: OfferViewDTO[]): Observable<OfferViewDTO[]> {
    return of(entities);
  }

  public duplicateOffer(offerId?: number, name?: string, copyCutOff?: boolean | null, copyAcceptance?: boolean | null, copyPricing?: boolean | null): Observable<OfferViewDTO> {
    return this.#apiProvider.post<OfferViewDTO>(`${this.offerDuplicateUrl(offerId)}`, { name, copyCutOff, copyAcceptance, copyPricing }).pipe(
      mapAsBody,
      map((responses: OfferViewDTO) => new OfferViewDTO(responses))
    );
  }

  public query(req?: any): Observable<HttpResponse<OfferViewDTO[]>> {
    const options = createRequestOption(req);
    return this.#apiProvider.get<OfferViewDTO[]>(this.resourceUrl, { params: options, observe: 'response' }).pipe(
      map((res: HttpResponse<OfferViewDTO[]>) => {
        return new HttpResponse<OfferViewDTO[]>({
          body: (res.body || []).map(v => new OfferViewDTO(v)),
          url: res.url as string,
          headers: res.headers,
          status: res.status,
        });
      })
    );
  }

  public delete(id: number): Observable<HttpResponse<{}>> {
    return this.#apiProvider.delete(`${this.resourceUrl}/${id}`);
  }

  public getTransporterOffersWithName(transporterId: number): Observable<OfferViewDTO[]> {
    return this.#apiProvider.get<OfferViewDTO[]>(this.offerUrl(transporterId), { observe: 'response' }).pipe(
      mapAsBody,
      map((responses: OfferViewDTO[]) => responses.map(val => new OfferViewDTO(val)))
    );
  }

  public getTransportOffer(offerId: number): Observable<OfferViewDTO> {
    return this.#apiProvider.publicGet<Offer>(this.offerId(offerId), { observe: 'response' }).pipe(
      mapAsBody,
      map((responses: OfferViewDTO) => new OfferViewDTO(responses))
    );
  }

  public setDefaultItemsForOffer(id: number): Observable<OfferViewDTO[]> {
    return this.#apiProvider.post<OfferViewDTO[]>(this.insuranceItemsUrl(id), null, { observe: 'response' }).pipe(
      mapAsBody,
      map((responses: OfferViewDTO[]) => responses.map(val => new OfferViewDTO(val)))
    );
  }
}
